/**
 * Cart Save and Forward
 * Handles saving carts, generating shareable links, and email forwarding
 */

class CartSaveForward {
  constructor() {
    this.config = this.getConfig();
    this.savedCartKey = 'rsw_saved_cart';
    this.init();
  }

  getConfig() {
    const configEl = document.querySelector('[data-cart-save-forward-config]');
    if (configEl) {
      try {
        return JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('Failed to parse cart config:', e);
      }
    }
    return {
      isCustomer: false,
      customerId: null,
      shopUrl: window.location.origin,
      cartUrl: '/cart'
    };
  }

  init() {
    this.attachEventListeners();
    this.checkForSharedCart();
    this.checkForSavedCart();
  }

  attachEventListeners() {
    // Save cart button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-save-btn]')) {
        e.preventDefault();
        this.saveCart();
      }
    });

    // Share cart button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-share-btn]')) {
        e.preventDefault();
        this.showShareModal();
      }
    });

    // Email cart button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-email-btn]')) {
        e.preventDefault();
        this.showEmailModal();
      }
    });

    // Load saved cart button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-load-btn]')) {
        e.preventDefault();
        this.loadSavedCart();
      }
    });

    // Copy link button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-copy-link-btn]')) {
        e.preventDefault();
        this.copyShareLink();
      }
    });

    // Modal close buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-modal-close]')) {
        e.preventDefault();
        this.closeAllModals();
      }
    });

    // Email form submission
    document.addEventListener('submit', (e) => {
      if (e.target.matches('[data-cart-email-form]')) {
        e.preventDefault();
        this.sendEmailCart(e.target);
      }
    });

    // Notification close
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-notification-close]')) {
        e.preventDefault();
        this.hideNotification();
      }
    });

    // Close modals on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllModals();
      }
    });
  }

  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      this.showNotification('Error loading cart', 'error');
      return null;
    }
  }

  async saveCart() {
    const cart = await this.getCurrentCart();
    if (!cart || cart.items.length === 0) {
      this.showNotification('Your cart is empty', 'error');
      return;
    }

    const cartData = {
      items: cart.items.map(item => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {}
      })),
      note: cart.note || '',
      saved_at: new Date().toISOString()
    };

    if (this.config.isCustomer) {
      // Save to customer metafield (requires server-side implementation)
      this.saveToCustomerAccount(cartData);
    } else {
      // Save to localStorage
      this.saveToLocalStorage(cartData);
    }
  }

  saveToLocalStorage(cartData) {
    try {
      localStorage.setItem(this.savedCartKey, JSON.stringify(cartData));
      this.showNotification('Cart saved successfully!', 'success');
      this.updateLoadButton(true);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      this.showNotification('Failed to save cart', 'error');
    }
  }

  saveToCustomerAccount(cartData) {
    // Save to localStorage as fallback since Shopify doesn't allow
    // direct customer metafield updates from frontend
    this.saveToLocalStorage(cartData);
    
    // In a production environment, you would send this to a server endpoint
    // that handles saving to customer metafields via Shopify Admin API
    // Example:
    // fetch('/apps/cart-saver/save', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ cart: cartData })
    // });
  }

  async loadSavedCart() {
    let cartData = null;

    if (this.config.isCustomer) {
      // Try to load from customer metafield first (would need server endpoint)
      // For now, fallback to localStorage
      cartData = this.loadFromLocalStorage();
    } else {
      cartData = this.loadFromLocalStorage();
    }

    if (!cartData) {
      this.showNotification('No saved cart found', 'error');
      return;
    }

    await this.restoreCart(cartData);
  }

  loadFromLocalStorage() {
    try {
      const data = localStorage.getItem(this.savedCartKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }

  async restoreCart(cartData) {
    try {
      // Clear current cart
      await fetch('/cart/clear.js', { method: 'POST' });

      // Add saved items
      const updates = {};
      cartData.items.forEach(item => {
        updates[item.id] = item.quantity;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw new Error('Failed to restore cart');

      // Update cart note if exists
      if (cartData.note) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: cartData.note })
        });
      }

      this.showNotification('Cart loaded successfully!', 'success');
      
      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Reload page to show updated cart
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error restoring cart:', error);
      this.showNotification('Failed to load cart', 'error');
    }
  }

  async showShareModal() {
    const cart = await this.getCurrentCart();
    if (!cart || cart.items.length === 0) {
      this.showNotification('Your cart is empty', 'error');
      return;
    }

    const shareLink = this.generateShareLink(cart);
    const modal = document.querySelector('[data-cart-share-modal]');
    const input = modal?.querySelector('[data-share-link-input]');
    
    if (input) {
      input.value = shareLink;
    }
    
    if (modal) {
      modal.classList.remove('cart-modal--hidden');
      document.body.style.overflow = 'hidden';
    }
  }

  generateShareLink(cart) {
    const cartData = {
      items: cart.items.map(item => ({
        id: item.variant_id,
        quantity: item.quantity
      }))
    };
    
    // Encode cart data to base64
    const encoded = btoa(JSON.stringify(cartData));
    return `${this.config.shopUrl}/?cart=${encodeURIComponent(encoded)}`;
  }

  copyShareLink() {
    const input = document.querySelector('[data-share-link-input]');
    if (!input) return;

    input.select();
    input.setSelectionRange(0, 99999); // For mobile devices

    try {
      document.execCommand('copy');
      this.showNotification('Link copied to clipboard!', 'success');
    } catch (error) {
      // Fallback for modern browsers
      navigator.clipboard.writeText(input.value).then(() => {
        this.showNotification('Link copied to clipboard!', 'success');
      }).catch(() => {
        this.showNotification('Failed to copy link', 'error');
      });
    }
  }

  showEmailModal() {
    const modal = document.querySelector('[data-cart-email-modal]');
    if (modal) {
      modal.classList.remove('cart-modal--hidden');
      document.body.style.overflow = 'hidden';
    }
  }

  async sendEmailCart(form) {
    const formData = new FormData(form);
    const email = formData.get('email');
    const message = formData.get('message');

    const cart = await this.getCurrentCart();
    if (!cart || cart.items.length === 0) {
      this.showNotification('Your cart is empty', 'error');
      return;
    }

    const shareLink = this.generateShareLink(cart);
    
    // Create email body
    const emailBody = `
${message ? message + '\n\n' : ''}
View and add items to your cart: ${shareLink}

Cart Contents:
${cart.items.map(item => `- ${item.title} (${item.quantity}x)`).join('\n')}

Total: ${this.formatMoney(cart.total_price)}
    `.trim();

    // In production, this would send to a backend endpoint
    // For now, we'll use mailto as a fallback
    const subject = encodeURIComponent('Shared Shopping Cart');
    const body = encodeURIComponent(emailBody);
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;
    
    window.location.href = mailtoLink;
    
    this.closeAllModals();
    this.showNotification('Opening email client...', 'success');
    
    // Note: In production, you'd want to implement a proper email service
    // using a backend endpoint that sends emails via SendGrid, Mailgun, etc.
  }

  async checkForSharedCart() {
    const urlParams = new URLSearchParams(window.location.search);
    const cartParam = urlParams.get('cart');
    
    if (!cartParam) return;

    try {
      const decoded = JSON.parse(atob(decodeURIComponent(cartParam)));
      
      // Clear current cart
      await fetch('/cart/clear.js', { method: 'POST' });
      
      // Add items from shared link
      const updates = {};
      decoded.items.forEach(item => {
        updates[item.id] = item.quantity;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw new Error('Failed to load shared cart');

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));

      this.showNotification('Shared cart loaded successfully!', 'success');
      
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('cart');
      window.history.replaceState({}, '', url);
      
      // Redirect to cart page
      setTimeout(() => {
        window.location.href = this.config.cartUrl;
      }, 1500);
    } catch (error) {
      console.error('Error loading shared cart:', error);
      this.showNotification('Failed to load shared cart', 'error');
      
      // Clean up URL even on error
      const url = new URL(window.location.href);
      url.searchParams.delete('cart');
      window.history.replaceState({}, '', url);
    }
  }

  checkForSavedCart() {
    const savedCart = this.loadFromLocalStorage();
    if (savedCart) {
      this.updateLoadButton(true);
    }
  }

  updateLoadButton(show) {
    const loadBtn = document.querySelector('[data-cart-load-btn]');
    if (loadBtn) {
      loadBtn.style.display = show ? 'block' : 'none';
    }
  }

  closeAllModals() {
    const modals = document.querySelectorAll('.cart-modal');
    modals.forEach(modal => {
      modal.classList.add('cart-modal--hidden');
    });
    document.body.style.overflow = '';
  }

  showNotification(message, type = 'success') {
    const notification = document.querySelector('[data-cart-notification]');
    const messageEl = notification?.querySelector('[data-notification-message]');
    const iconEl = notification?.querySelector('[data-notification-icon]');

    if (!notification || !messageEl) return;

    messageEl.textContent = message;
    
    // Set icon based on type
    if (iconEl) {
      iconEl.innerHTML = type === 'success' 
        ? '✓' 
        : '✕';
    }

    notification.className = `cart-notification cart-notification--${type}`;
    notification.classList.remove('cart-notification--hidden');

    // Auto-hide after 5 seconds
    setTimeout(() => this.hideNotification(), 5000);
  }

  hideNotification() {
    const notification = document.querySelector('[data-cart-notification]');
    if (notification) {
      notification.classList.add('cart-notification--hidden');
    }
  }

  formatMoney(cents) {
    return (cents / 100).toFixed(2);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartSaveForward();
  });
} else {
  new CartSaveForward();
}

